import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const goalsApi = createApi({
  reducerPath: "goalsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Goals", "GoalStats"],
  endpoints: (builder) => ({
    // Get all goals
    getAllGoals: builder.query({
      query: (params) => ({
        url: "/goals",
        params,
      }),
      transformResponse: (response) => response.data || { goals: [], stats: {} },
      providesTags: ["Goals"],
    }),

    // Get goal stats
    getGoalStats: builder.query({
      query: () => "/goals/stats",
      transformResponse: (response) => response.data || {},
      providesTags: ["GoalStats"],
    }),

    // Get single goal
    getGoalById: builder.query({
      query: (id) => `/goals/${id}`,
      transformResponse: (response) => response.data?.goal || null,
      providesTags: (result, error, id) => [{ type: "Goals", id }],
    }),

    // Create goal
    createGoal: builder.mutation({
      query: (data) => ({
        url: "/goals",
        method: "POST",
        body: data,
      }),
      transformResponse: (response) => response.data?.goal || response,
      invalidatesTags: ["Goals", "GoalStats"],
    }),

    // Update goal
    updateGoal: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/goals/${id}`,
        method: "PUT",
        body: data,
      }),
      transformResponse: (response) => response.data?.goal || response,
      invalidatesTags: (result, error, { id }) => [
        { type: "Goals", id },
        "Goals",
        "GoalStats",
      ],
    }),

    // Update goal progress
    updateGoalProgress: builder.mutation({
      query: ({ id, current }) => ({
        url: `/goals/${id}/progress`,
        method: "PATCH",
        body: { current },
      }),
      transformResponse: (response) => response.data?.goal || response,
      invalidatesTags: (result, error, { id }) => [
        { type: "Goals", id },
        "Goals",
        "GoalStats",
      ],
    }),

    // Delete goal
    deleteGoal: builder.mutation({
      query: (id) => ({
        url: `/goals/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Goals", "GoalStats"],
    }),

    // Sync goals with real CRM data
    syncGoals: builder.mutation({
      query: () => ({
        url: "/goals/sync",
        method: "POST",
      }),
      invalidatesTags: ["Goals", "GoalStats"],
    }),
  }),
});

export const {
  useGetAllGoalsQuery,
  useGetGoalStatsQuery,
  useGetGoalByIdQuery,
  useCreateGoalMutation,
  useUpdateGoalMutation,
  useUpdateGoalProgressMutation,
  useDeleteGoalMutation,
  useSyncGoalsMutation,
} = goalsApi;
